// Record a staff document ID check (spec/07 §10, spec/13 §8, F-CMP-02): the member looked at a physical
// ID and records its type and the **last four characters** of its number — nothing more of the number
// is ever typed or sent — and, optionally, a photo of it. A short form, shown in place in the player dialog's ID
// check section, never a dialog on the player dialog (BRAND.md §6.1, 2026-10-07).
//
// The photo travels in the request itself, base64, and the server encrypts it into the private bucket
// (`routes/id-checks.ts`): there is no signed upload to make, and the browser never sees the bucket.
// The create needs an `Idempotency-Key`; the key is kept for as long as the same body is being sent, so
// a retry after a lost answer replays the check rather than recording a second one.

import type { IdCheckPhotoUpload, IdDocumentType, RecordIdCheck } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { useRecordIdCheck } from './queries';

/** The documents staff may record (the contract's `ID_DOCUMENT_TYPES`, spec/07 §10). */
const DOCUMENT_TYPES = ['aadhaar', 'pan', 'passport', 'driving_licence', 'voter_id', 'other'] as const satisfies readonly IdDocumentType[];

/** The contract's `MAX_ID_PHOTO_BYTES` and `IMAGE_CONTENT_TYPES` (`routes/id-checks.ts`, `routes/files.ts`). */
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const satisfies readonly IdCheckPhotoUpload['contentType'][];

/** The contract's `DOCUMENT_LAST4_PATTERN`. */
const LAST4 = /^[A-Za-z0-9]{4}$/;

/** The form, in place: Cancel and a recorded check both call `onDone`. */
export function IdCheckForm({ playerId, onDone }: { readonly playerId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problem = useProblem();
  const record = useRecordIdCheck(playerId);
  const [documentType, setDocumentType] = useState<IdDocumentType>('aadhaar');
  const [last4, setLast4] = useState('');
  const [photo, setPhoto] = useState<(IdCheckPhotoUpload & { readonly name: string }) | null>(null);
  const [errors, setErrors] = useState<{ last4?: string; photo?: string; form?: string }>({});
  // One key per body: the same body sent again (a retry) reuses it, a changed body gets a new one.
  const attempt = useRef<{ key: string; body: string } | null>(null);

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    if (!(PHOTO_TYPES as readonly string[]).includes(file.type)) {
      setErrors((current) => ({ ...current, photo: t('players.idCheck.photoType') }));
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setErrors((current) => ({ ...current, photo: t('players.idCheck.photoTooLarge') }));
      return;
    }
    void toBase64(file).then(
      (data) => {
        setErrors((current) => ({ ...current, photo: undefined }));
        setPhoto({ contentType: file.type as IdCheckPhotoUpload['contentType'], data, name: file.name });
      },
      () => setErrors((current) => ({ ...current, photo: t('players.idCheck.photoUnreadable') })),
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = last4.trim().toUpperCase();
    if (!LAST4.test(value)) {
      setErrors({ last4: t('players.idCheck.last4Invalid') });
      return;
    }
    setErrors({});
    const body: RecordIdCheck = {
      documentType,
      documentLast4: value,
      ...(photo === null ? {} : { photo: { contentType: photo.contentType, data: photo.data } }),
    };
    const serial = JSON.stringify(body);
    if (attempt.current?.body !== serial) attempt.current = { key: crypto.randomUUID(), body: serial };
    record.mutate(
      { body, idempotencyKey: attempt.current.key },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('players.idCheck.recorded') });
          onDone();
        },
        onError: (failure) => {
          const last4Problem = problem.fieldMessage(failure, 'documentLast4');
          setErrors(last4Problem === undefined ? { form: problem.text(failure, { fallback: t('players.idCheck.recordFailed') }) } : { last4: last4Problem });
        },
      },
    );
  };

  return (
    <form className="console-form" aria-label={t('players.idCheck.recordTitle')} noValidate onSubmit={submit}>
      <Notice role="note">{t('players.idCheck.recordHint')}</Notice>
      <SelectField
        label={t('players.idCheck.documentType')}
        value={documentType}
        onValueChange={(value) => setDocumentType(value as IdDocumentType)}
        options={DOCUMENT_TYPES.map((type) => ({ value: type, label: t(`players.idCheck.document.${type}`) }))}
      />
      <TextField
        label={t('players.idCheck.last4')}
        description={t('players.idCheck.last4Hint')}
        required
        maxLength={4}
        autoComplete="off"
        spellCheck={false}
        value={last4}
        onChange={(event) => setLast4(event.target.value.toUpperCase())}
        {...(errors.last4 === undefined ? {} : { error: errors.last4 })}
      />
      <div className="console-people-idphoto">
        <span className="console-people-idphoto__label">{t('players.idCheck.photo')}</span>
        <span className="console-subtle">{t('players.idCheck.photoHint')}</span>
        <div className="console-form__actions">
          <label className="clav-button clav-button--secondary clav-button--sm console-file">
            <input type="file" accept={PHOTO_TYPES.join(',')} className="console-file__input" onChange={onFile} />
            {photo === null ? t('players.idCheck.addPhoto') : t('players.idCheck.replacePhoto')}
          </label>
          {photo === null ? null : (
            <>
              <span className="console-muted">{photo.name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPhoto(null)}>
                {t('players.idCheck.removePhoto')}
              </Button>
            </>
          )}
        </div>
        {errors.photo === undefined ? null : (
          <p className="console-people-idphoto__error" role="alert">
            {errors.photo}
          </p>
        )}
      </div>
      {errors.form === undefined ? null : <Notice tone="danger" role="alert" title={errors.form} />}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={record.isPending}>
          {t('players.idCheck.record')}
        </Button>
      </div>
    </form>
  );
}

/** A file's bytes as padded base64 (RFC 4648 §4), without the `data:` prefix a data URL carries. */
function toBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      const comma = url.indexOf(',');
      if (comma < 0) reject(new Error('unreadable'));
      else resolve(url.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('unreadable'));
    reader.readAsDataURL(file);
  });
}
